import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { createPopSound } from '../assets/sounds/pop';
import { Link } from 'react-router-dom';

interface BubbleProps {
  isPopped: boolean;
  color: string;
}

const MainContent = styled.main`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: #666;
  max-width: 600px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
`;

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    padding: 0 1rem;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 250px;
  
  @media (max-width: 768px) {
    width: 100%;
    order: -1;
    margin-bottom: 2rem;
  }
`;

const NavButton = styled(Link)`
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  text-decoration: none;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const GameArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
`;

const Controls = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 250px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const ControlTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--primary);
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const SpeedControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SpeedSlider = styled.input`
  width: 100%;
  height: 6px;
  background: linear-gradient(to right, var(--primary), var(--secondary));
  border-radius: 3px;
  appearance: none;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`;

const SpeedValue = styled.div`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  padding: 1.5rem;
  border-radius: 16px;
  background: #f0f0f0;
  box-shadow: 
    inset 0 0 20px rgba(0, 0, 0, 0.1),
    0 10px 20px rgba(0, 0, 0, 0.2);
  aspect-ratio: 1;
  width: min(90vw, 500px);
  margin: 0 auto;
  
  @media (max-width: 480px) {
    width: 100%;
    gap: 0.35rem;
    padding: 1rem;
  }
`;

const Button = styled(motion.button)<{ active?: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? 'var(--secondary)' : 'var(--primary)'};
  color: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const Bubble = styled(motion.button)<BubbleProps>`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  border: none;
  background: ${props => props.color};
  cursor: pointer;
  position: relative;
  transform-style: preserve-3d;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.isPopped ? 0.75 : 1};
  transform: ${props => props.isPopped ? 
    'translateZ(-12px) scale(0.95)' : 
    'translateZ(12px) scale(1)'
  };
  box-shadow: ${props => props.isPopped ? 
    'inset 0 6px 10px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1)' :
    '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1)'
  };

  &:before {
    content: '';
    position: absolute;
    width: 92%;
    height: 92%;
    border-radius: 50%;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, ${props => props.isPopped ? 0.2 : 0.6}) 0%,
      transparent ${props => props.isPopped ? '60%' : '70%'}
    );
    top: 4%;
    left: 4%;
    transform: ${props => props.isPopped ? 
      'translateZ(1px) scale(0.98)' : 
      'translateZ(4px) scale(1)'
    };
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    filter: ${props => props.isPopped ? 'blur(1px)' : 'none'};
  }

  &:hover {
    transform: ${props => props.isPopped ? 
      'translateZ(-10px) scale(0.97)' : 
      'translateZ(14px) scale(1.02)'
    };
  }
`;

const pulseAnimation = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
    }
  }

  @keyframes textPulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ResetButton = styled(Button)`
  background: var(--primary);
  animation: pulse 2s infinite;
  position: relative;
  padding: 1rem 2rem;
  ${pulseAnimation}

  span {
    display: inline-block;
    animation: textPulse 2s infinite;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  &:hover {
    animation: none;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    span {
      animation: none;
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const InspirationBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 16px;
  margin: 2rem 0;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const InspirationText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  line-height: 1.6;
  
  strong {
    color: #ff4b4b;
  }
`;

const Quote = styled.blockquote`
  font-style: italic;
  color: var(--secondary);
  font-size: 1.1rem;
  margin-top: 1.5rem;
  padding: 1rem;
  border-left: 4px solid var(--primary);
  background: rgba(255, 255, 255, 0.05);
`;

const PopFidget: React.FC = () => {
  const [bubbles, setBubbles] = useState<boolean[]>([]);
  const [isAutoPopping, setIsAutoPopping] = useState(false);
  const [speed, setSpeed] = useState(50);
  const [isRepeat, setIsRepeat] = useState(false);
  const autoPopTimeoutRef = useRef<number | null>(null);
  const bubblesRef = useRef<boolean[]>([]);

  useEffect(() => {
    bubblesRef.current = bubbles;
  }, [bubbles]);
  
  const colors = [
    '#FF4B4B', // Red
    '#FF8A3D', // Orange
    '#FFD93D', // Yellow
    '#4CAF50', // Green
    '#2196F3', // Blue
    '#9C27B0', // Purple
  ];

  useEffect(() => {
    resetGrid();
    return () => {
      if (autoPopTimeoutRef.current) {
        clearTimeout(autoPopTimeoutRef.current);
      }
    };
  }, []);

  const resetGrid = useCallback(() => {
    setBubbles(new Array(36).fill(false));
    bubblesRef.current = new Array(36).fill(false);
    if (autoPopTimeoutRef.current) {
      clearTimeout(autoPopTimeoutRef.current);
    }
  }, []);

  const popBubble = (index: number) => {
    setBubbles(prev => {
      const newBubbles = [...prev];
      newBubbles[index] = true;
      return newBubbles;
    });
    createPopSound();
    
    // Add vibration feedback for mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // 50ms vibration
    }
  };

  const getColor = (index: number) => {
    const row = Math.floor(index / 6);
    return colors[row];
  };

  const getRandomUnpoppedIndex = () => {
    const unpoppedIndices = bubblesRef.current
      .map((isPopped, index) => ({ isPopped, index }))
      .filter(({ isPopped }) => !isPopped)
      .map(({ index }) => index);

    if (unpoppedIndices.length === 0) {
      return -1;
    }

    return unpoppedIndices[Math.floor(Math.random() * unpoppedIndices.length)];
  };

  const startAutoPop = () => {
    setIsAutoPopping(true);
    
    const autoPop = () => {
      const nextIndex = getRandomUnpoppedIndex();
      
      if (nextIndex === -1) {
        // All bubbles are popped
        if (isRepeat) {
          resetGrid();
          // Continue auto-popping after a short delay
          autoPopTimeoutRef.current = setTimeout(() => {
            const firstPop = getRandomUnpoppedIndex();
            if (firstPop !== -1) {
              popBubble(firstPop);
              autoPopTimeoutRef.current = setTimeout(autoPop, 1100 - speed * 10);
            }
          }, 1000); // Wait 1 second before starting new round
        } else {
          setIsAutoPopping(false);
        }
        return;
      }

      popBubble(nextIndex);
      autoPopTimeoutRef.current = setTimeout(autoPop, 1100 - speed * 10);
    };

    // Start the first pop
    const firstIndex = getRandomUnpoppedIndex();
    if (firstIndex !== -1) {
      popBubble(firstIndex);
      autoPopTimeoutRef.current = setTimeout(autoPop, 1100 - speed * 10);
    }
  };

  const stopAutoPop = () => {
    setIsAutoPopping(false);
    if (autoPopTimeoutRef.current) {
      clearTimeout(autoPopTimeoutRef.current);
    }
  };

  return (
    <MainContent>
      <Header>
        <Title>Pop Fidget Toy - Instant Stress Relief</Title>
        <Description>
          Experience immediate stress relief with our interactive pop fidget toy. 
          Click the colorful bubbles to hear satisfying pop sounds, or use auto-pop 
          mode for a mesmerizing display. Perfect for anxiety relief, focus improvement, 
          and relaxation.
        </Description>
      </Header>

      <Container>
        <NavigationButtons>
          <NavButton to="/breathing">Stress Relief Breathing</NavButton>
          <NavButton to="/tips">Stress Relief Tips</NavButton>
          <NavButton to="/herbs">Stress Relief Herbs</NavButton>
        </NavigationButtons>

        <GameArea>
          <ResetButton 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }} 
            onClick={() => {
              resetGrid();
              setIsAutoPopping(false);
            }}
          >
            <span>Reset</span>
          </ResetButton>

          <Grid
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            role="grid"
            aria-label="Pop Fidget Toy Grid"
          >
            <AnimatePresence>
              {bubbles.map((isPopped, index) => (
                <Bubble
                  key={index}
                  isPopped={isPopped}
                  color={getColor(index)}
                  onClick={() => !isAutoPopping && popBubble(index)}
                  whileTap={{ 
                    scale: 0.85,
                    translateZ: '-14px',
                  }}
                  initial={false}
                  animate={{
                    scale: isPopped ? 0.95 : 1,
                    translateZ: isPopped ? -12 : 12,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 25
                    }
                  }}
                  aria-label={`Bubble ${index + 1} ${isPopped ? 'popped' : 'unpopped'}`}
                  role="button"
                />
              ))}
            </AnimatePresence>
          </Grid>

          <InspirationBox>
            <InspirationText>
              If you need help, seek help, <strong>you are not alone!</strong> A bright future is just around the corner, I promise!
            </InspirationText>
            <Quote>
              "Failure is not Fatal, Success is not Final, it is the Courage to Continue that Counts" - Winston Churchill
            </Quote>
          </InspirationBox>
        </GameArea>

        <Controls>
          <ControlTitle>Auto-Pop Controls</ControlTitle>
          
          <SpeedControl>
            <label htmlFor="speed">Speed</label>
            <SpeedSlider 
              type="range" 
              id="speed"
              min="1" 
              max="100" 
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              aria-label="Auto-pop speed control"
            />
            <SpeedValue>{speed}%</SpeedValue>
          </SpeedControl>

          <Button
            active={isRepeat}
            onClick={() => setIsRepeat(!isRepeat)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            aria-pressed={isRepeat}
          >
            {isRepeat ? 'Repeat: ON' : 'Repeat: OFF'}
          </Button>

          <Button
            active={isAutoPopping}
            onClick={isAutoPopping ? stopAutoPop : startAutoPop}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            aria-pressed={isAutoPopping}
          >
            {isAutoPopping ? 'Stop Auto-Pop' : 'Start Auto-Pop'}
          </Button>
        </Controls>
      </Container>
    </MainContent>
  );
};

export default PopFidget; 